import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Truck, ShieldCheck, Euro, Phone, ArrowLeft } from 'lucide-react';
import catalogueStructure from '../data/catalogueStructure';
import { formatProductPrice } from '../utils/productPrice';
import { productPath } from '../utils/productPath';
import { SITE_URL, SITE_NAME, BUSINESS } from '../data/seoConfig';
import JsonLd from '../components/JsonLd';
import NotFoundPage from './NotFoundPage';

const API = process.env.REACT_APP_BACKEND_URL;

const priceValue = (product) => {
  const m = /(\d+(?:[.,]\d+)?)/.exec(product.price || '');
  return m ? m[1].replace(',', '.') : null;
};

const dimensions = (name) => {
  const m = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i.exec(name || '');
  if (!m) return null;
  const w = parseFloat(m[1].replace(',', '.'));
  const l = parseFloat(m[2].replace(',', '.'));
  return { w, l, surface: Math.round(w * l) };
};

const ProductPage = () => {
  const { category, subcategory, slug } = useParams();
  const [products, setProducts] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/products`)
      .then((r) => r.json())
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]));
  }, []);

  if (products === null) {
    return (
      <div className="min-h-screen bg-white pt-32 text-center" data-testid="product-loading">
        <p className="text-gray-400">Chargement de la fiche produit...</p>
      </div>
    );
  }

  const product = products.find((p) => (p.slug || p.id) === slug);
  if (!product) {
    const renamed = products.find((p) => (p.old_slugs || []).includes(slug));
    if (renamed) return <Navigate to={productPath(renamed)} replace />;
    return <NotFoundPage />;
  }
  if (product.category !== category || product.subcategory !== subcategory) {
    return <Navigate to={productPath(product)} replace />;
  }

  const categoryName = catalogueStructure.find((c) => c.slug === product.category)?.name || product.category;
  const subcat = catalogueStructure.find((c) => c.slug === product.category)?.subcategories?.find((s) => s.slug === product.subcategory);
  const subcategoryName = subcat ? subcat.name : (product.subcategory || '').replace(/-/g, ' ');
  const path = productPath(product);
  const url = SITE_URL + path;
  const dims = dimensions(product.name);
  const price = priceValue(product);
  const isStructure = product.category === 'barnums-tentes';

  const related = products
    .filter((p) => p.subcategory === product.subcategory && p.id !== product.id)
    .slice(0, 3);

  const title = `Location ${product.name} au Havre et en Normandie | ${SITE_NAME}`;
  const metaDescription = (product.description || '').slice(0, 300);

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image,
    sku: product.id,
    category: categoryName + ' / ' + subcategoryName,
    brand: { '@type': 'Brand', name: SITE_NAME },
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency: 'EUR',
      ...(price ? { price } : {}),
      availability: 'https://schema.org/InStock',
      businessFunction: 'http://purl.org/goodrelations/v1#LeaseOut',
      seller: { '@type': 'Organization', name: BUSINESS.legalName, url: SITE_URL },
      areaServed: { '@type': 'AdministrativeArea', name: 'Normandie' },
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: SITE_URL + '/' },
      { '@type': 'ListItem', position: 2, name: 'Catalogue', item: SITE_URL + '/catalogue' },
      { '@type': 'ListItem', position: 3, name: categoryName, item: SITE_URL + '/catalogue/' + product.category },
      { '@type': 'ListItem', position: 4, name: subcategoryName, item: SITE_URL + '/catalogue/' + product.category + '/' + product.subcategory },
      { '@type': 'ListItem', position: 5, name: product.name, item: url },
    ],
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="product-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={product.image} />
        <meta property="og:type" content="product" />
      </Helmet>
      <JsonLd data={productJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <nav className="flex flex-wrap items-center gap-2 text-sm text-gray-400 mb-8" aria-label="Fil d'ariane">
          <Link to="/" className="hover:text-orange-500">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/catalogue" className="hover:text-orange-500">Catalogue</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={'/catalogue/' + product.category} className="hover:text-orange-500">{categoryName}</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={'/catalogue/' + product.category + '/' + product.subcategory} className="hover:text-orange-500" data-testid="breadcrumb-subcategory">
            {subcategoryName}
          </Link>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="bg-gray-50 rounded-2xl overflow-hidden">
            <img
              src={product.image}
              alt={`Location ${product.name} – ${categoryName} en Normandie`}
              className="w-full aspect-[4/3] object-contain p-4"
            />
          </div>

          <div>
            <span className="text-xs uppercase tracking-widest text-orange-500 font-semibold">{subcategoryName}</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-2 mb-5" data-testid="product-h1">
              {product.name}
            </h1>
            <p className="text-base text-gray-600 leading-relaxed mb-7">{product.description}</p>

            <div className="flex flex-wrap items-end gap-6 mb-8">
              {formatProductPrice(product) && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Tarif location</p>
                  <p className="text-3xl font-bold text-orange-500" data-testid="product-price">
                    {formatProductPrice(product)}
                  </p>
                </div>
              )}
              {product.caution && !/^0\s*€?$/.test(product.caution.trim()) && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Caution</p>
                  <p className="text-lg font-semibold text-gray-700">{product.caution}</p>
                </div>
              )}
              {dims && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Surface couverte</p>
                  <p className="text-lg font-semibold text-gray-700">{dims.surface} m²</p>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3 mb-8">
              <Link
                to={'/devis?produit=' + encodeURIComponent(product.name)}
                className="inline-flex items-center gap-2 px-7 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-colors"
                data-testid="product-cta-devis"
              >
                Demander ce produit <ChevronRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+33623893298"
                className="inline-flex items-center gap-2 px-7 py-3 border border-gray-300 text-gray-700 font-medium rounded-full hover:border-orange-400 hover:text-orange-600 transition-colors"
                data-testid="product-cta-phone"
              >
                <Phone className="w-4 h-4" /> 06 23 89 32 98
              </a>
            </div>

            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start gap-3">
                <Truck className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                {isStructure
                  ? 'Livraison, montage et démontage depuis notre dépôt de Gainneville, dans tout le Havre et la Normandie.'
                  : 'Livraison et reprise depuis notre dépôt de Gainneville, dans tout le Havre et la Normandie, ou retrait sur place.'}
              </li>
              <li className="flex items-start gap-3">
                <ShieldCheck className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                {isStructure
                  ? 'Matériel conforme : toiles ignifugées M2 (NF P92-507), registre de sécurité et certificat de montage sur demande.'
                  : 'Matériel contrôlé et nettoyé avant chaque location, remplacé immédiatement en cas de casse constatée au départ.'}
              </li>
              <li className="flex items-start gap-3">
                <Euro className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                Réservation à 30 % d'acompte, caution restituée sous 7 jours, mise à disposition du vendredi au lundi.
              </li>
            </ul>
          </div>
        </div>

        {/* Texte local */}
        <section className="mt-16 max-w-3xl" data-testid="product-local-text">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
            Louer {product.name.toLowerCase()} au Havre et en Normandie
          </h2>
          <p className="text-base text-gray-600 leading-relaxed mb-4">
            Barnum Événement loue {product.name.toLowerCase()} pour les mariages, anniversaires, séminaires et
            événements associatifs organisés au Havre, à Gainneville, Gonfreville-l'Orcher, Montivilliers, Fécamp,
            Étretat, Honfleur, Rouen, Dieppe, Caen, Deauville et Lisieux. Nous livrons, installons et récupérons
            le matériel aux dates convenues, sans que vous ayez à prévoir de véhicule ni de main-d'œuvre.
            {dims
              ? ` Cette structure de ${dims.w} × ${dims.l} m couvre ${dims.surface} m², soit environ ${Math.floor(dims.surface / 1.5)} convives assis ou ${Math.floor(dims.surface)} personnes en cocktail debout.`
              : ''}
          </p>
          <p className="text-base text-gray-600 leading-relaxed">
            Vous hésitez sur la taille ou la configuration ? Consultez notre{' '}
            <Link to="/guide-technique" className="text-orange-600 hover:underline">guide technique et sécurité</Link>
            {isStructure ? (
              <>
                {' '}ou utilisez le{' '}
                <Link to="/blog/taille-barnum-mariage-100-personnes" className="text-orange-600 hover:underline">
                  calculateur de surface
                </Link>{' '}
                pour vérifier que ce matériel correspond à votre nombre d'invités.
              </>
            ) : (
              <>
                {' '}ou demandez-nous conseil : nous ajustons les quantités à votre nombre d'invités.
              </>
            )}
          </p>
        </section>

        {/* Produits liés */}
        {related.length > 0 && (
          <section className="mt-16" data-testid="product-related">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-6">
              Autres {subcategoryName.toLowerCase()} disponibles
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={productPath(p)}
                  className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={'related-product-' + p.id}
                >
                  <img src={p.image} alt={p.name} className="w-full aspect-[4/3] object-contain p-3" loading="lazy" />
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">
                      {p.name}
                    </h3>
                    {formatProductPrice(p) && (
                      <p className="text-sm text-orange-500 font-bold mt-1">{formatProductPrice(p)}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <Link
          to={'/catalogue/' + product.category + '/' + product.subcategory}
          className="inline-flex items-center gap-2 mt-12 text-sm font-medium text-orange-600 hover:underline"
          data-testid="back-to-subcategory"
        >
          <ArrowLeft className="w-4 h-4" /> Retour à {subcategoryName.toLowerCase()}
        </Link>
      </div>
    </div>
  );
};

export default ProductPage;
